import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ordo/ui/card'
import { Skeleton } from '@ordo/ui/skeleton'
import { Link } from 'react-router'

import { useSchoolOnboardingTasksQuery } from '@/api/school-onboarding'
import { missingChefOnboardingDocuments } from '@/lib/chefs/onboarding-documents'

export function ChefOnboardingDocumentsCard({ organizationId }: { organizationId: string }) {
  const query = useSchoolOnboardingTasksQuery()
  const tasks = (query.data?.tasks ?? []).filter(
    (task) =>
      task.organizationId === organizationId &&
      task.formDefinition.fields.some((field) => field.type === 'document_upload'),
  )
  const missing = missingChefOnboardingDocuments(
    [{ id: organizationId, name: '', profileCodes: ['CHEF_ADMIN'] }],
    tasks,
  )
  const first = tasks.find((task) => missing.some((entry) => entry.taskId === task.id)) ?? tasks[0]
  const missingCount = missing.reduce((count, task) => count + task.labels.length, 0)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Kitchen documents</CardTitle>
        <CardDescription>Upload and review the documents for your kitchen.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {query.isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : query.error ? (
          <>
            <p className="text-sm text-destructive">Couldn’t load your documents.</p>
            <Button type="button" variant="outline" onClick={() => void query.refetch()}>
              Retry
            </Button>
          </>
        ) : first ? (
          <>
            <p className="text-sm text-muted-foreground">
              {missing.length
                ? `${missingCount} ${missingCount === 1 ? 'document still needs' : 'documents still need'} to be uploaded.`
                : 'Your requested documents are on file.'}
            </p>
            <Button asChild variant={missing.length ? 'default' : 'outline'}>
              <Link to={`/onboarding/${encodeURIComponent(first.id)}`}>
                {missing.length ? 'Upload documents' : 'Review documents'}
              </Link>
            </Button>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">No documents are currently requested.</p>
        )}
      </CardContent>
    </Card>
  )
}
